import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useDebounced } from "@/hooks/useDebounced";
import { useSessionDraft } from "@/hooks/useSessionDraft";
import {
  api,
  ApiError,
  type ChannelMode,
  type ChannelPlatformStatus,
  type ChannelSurfaceType,
} from "@/lib/api";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { PLATFORM_LABEL, ROW_SURFACE_LABEL } from "./channel-mode";
import { ModePicker } from "./ModePicker";

const KNOWN_SURFACES = new Set<ChannelSurfaceType>(["dm", "group_dm", "channel", "private_channel"]);

const asKnownSurface = (s: string): ChannelSurfaceType | undefined =>
  KNOWN_SURFACES.has(s as ChannelSurfaceType) ? (s as ChannelSurfaceType) : undefined;

interface AddTarget {
  id: string;
  displayName: string | null;
  surfaceType?: string;
}

// Where the user finds the ID they're asked to paste, per platform.
const MANUAL_ID_HINT: Record<string, { channel: string; user: string }> = {
  slack: {
    channel: "Find the channel ID at the bottom of the channel's details in Slack.",
    user: "Find the member ID in the person's Slack profile (⋯ → Copy member ID).",
  },
  gchat: {
    channel: "Use the space ID from the Google Chat URL (e.g. spaces/AAAA1234).",
    user: "Use the person's Google Chat user ID (e.g. users/1234567890).",
  },
  teams: {
    channel: "Use the channel or chat ID from the Teams link (e.g. 19:…@thread.tacv2).",
    user: "Use the person's Teams user ID (their Microsoft Entra object ID).",
  },
};

/**
 * Add channels/people before Boo has seen them. Platforms whose directory the
 * Boo can browse (Slack today) get a searchable picker; add-by-ID entry is
 * always available besides it — the directory is cached for a few minutes, so
 * a private or brand-new channel may only be reachable by its ID. Typed IDs are
 * resolved live (debounced) via the channels/meta lookup to confirm the match
 * and pick up the real name; ID/name drafts survive a reload via
 * sessionStorage. kind="channel" adds channels with a chosen mode, kind="user"
 * registers a person as blocked or allowed ahead of their first message.
 */
export function ChannelDirectoryDialog({
  containerId,
  kind,
  platforms,
  defaultBlocked = true,
  blockNewUsers = false,
  trigger,
  open: openProp,
  onOpenChange,
}: {
  containerId: string;
  kind: "channel" | "user";
  /** Enabled chat platforms — must be non-empty. */
  platforms: ChannelPlatformStatus[];
  /** kind="user": preselected treatment for added people. */
  defaultBlocked?: boolean;
  /** kind="user": drives the contextual caption under the treatment picker. */
  blockNewUsers?: boolean;
  /** Omit when the caller opens the dialog itself through `open`. */
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp ?? ownOpen;
  const setOpen = (next: boolean) => {
    setOwnOpen(next);
    onOpenChange?.(next);
  };
  const [platform, setPlatform] = useState(platforms[0]?.platform ?? "slack");
  const [searchInput, setSearchInput] = useState("");
  const [mode, setMode] = useState<ChannelMode>("mentions");
  const [blocked, setBlocked] = useState(defaultBlocked);
  // Drafts, not state: an accidental reload mid-paste shouldn't eat a long
  // opaque ID. Scoped per Boo/kind/platform; cleared on successful add.
  const draftKey = `add-${kind}:${containerId}:${platform}`;
  const [manualId, setManualId] = useSessionDraft(`${draftKey}:id`);
  const [manualName, setManualName] = useSessionDraft(`${draftKey}:name`);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  // Rows added in this dialog session keep an "Added ✓" mark instead of
  // instantly flipping to "Already listed".
  const [addedKeys, setAddedKeys] = useState<ReadonlySet<string>>(new Set());
  const q = useDebounced(searchInput.trim(), 250);

  const status = platforms.find((p) => p.platform === platform) ?? platforms[0];
  const hasDirectory = status?.directorySupported === true;

  const dir = useQuery({
    queryKey: ["channel-directory", containerId, platform, q],
    queryFn: () => api.containers.channels.directory(containerId, platform, q || undefined),
    enabled: open && hasDirectory,
    // Every debounced q is a new query key; without a placeholder the picker
    // would flip back to the loading state on each search — unmounting the
    // input mid-typing (focus loss) and collapsing the dialog. Keep the last
    // results on screen (dimmed below) until the new ones land.
    placeholderData: keepPreviousData,
    // A cold brain cache answers at once with the rows walked so far; keep
    // asking until the walk is done.
    refetchInterval: (query) => (query.state.data?.progress?.done === false ? 1000 : false),
  });
  const progress = dir.data?.progress;
  const loadingMore = progress?.done === false;
  const progressMessage = loadingMore
    ? `Loading ${kind === "channel" ? "channels" : "people"}… ${(kind === "channel"
        ? progress.channels
        : progress.users
      ).toLocaleString()} so far`
    : null;
  // The brain is the authority — it may report unsupported even when the
  // platform probe claimed directory support (e.g. credentials moved).
  const usePicker = hasDirectory && dir.data?.supported !== false;

  // Live lookup of the typed ID (channels only — there's no user meta
  // endpoint). Debounced so paste-then-fix doesn't fan out into a platform
  // call per keystroke; react-query dedupes repeats of the same id.
  const lookupId = useDebounced(manualId.trim(), 400);
  const meta = useQuery({
    queryKey: ["channel-meta", containerId, platform, lookupId],
    queryFn: () => api.containers.channels.meta(containerId, platform, lookupId),
    enabled: open && kind === "channel" && lookupId.length > 0,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  // Only trust the result once the debounce has caught up with the input.
  const lookupCurrent = kind === "channel" && lookupId.length > 0 && manualId.trim() === lookupId;
  const metaRow = lookupCurrent && meta.data?.supported === true ? meta.data : undefined;
  // 404 = the platform doesn't know the id (or the lookup failed) — warn, but
  // keep Add enabled. Anything else (outdated brain, unreachable) stays
  // silent: the lookup is a nicety, adding by ID must still work without it.
  const metaNotFound =
    lookupCurrent && meta.error instanceof ApiError && meta.error.status === 404;

  // The manual name field is a fallback, not a default: channels take their
  // name from the live lookup, and the field only appears when the name
  // genuinely can't be resolved — no resolver on the platform, lookup failed,
  // or the id resolved nameless (Slack DMs). Sticky once shown so it doesn't
  // flicker away while a retyped id re-checks; reset on platform switch and
  // dialog close. People always get the field (there's no user lookup).
  const nameLookupFailed =
    lookupCurrent &&
    !meta.isFetching &&
    (meta.isError ||
      meta.data?.supported === false ||
      (meta.data?.supported === true && meta.data.displayName == null));
  const [nameFieldShown, setNameFieldShown] = useState(false);
  const [prevPlatform, setPrevPlatform] = useState(platform);
  if (prevPlatform !== platform) {
    setPrevPlatform(platform);
    setNameFieldShown(false);
  }
  if (kind === "channel" && nameLookupFailed && !nameFieldShown) setNameFieldShown(true);
  // A persisted draft name always surfaces — never submit a value the user
  // can't see.
  const showNameInput = kind === "user" || nameFieldShown || manualName.trim().length > 0;

  const add = useMutation({
    mutationFn: (target: AddTarget) =>
      kind === "channel"
        ? api.containers.channels.add(containerId, {
            platform,
            channelOrSpaceId: target.id,
            mode,
            surfaceType: target.surfaceType ? asKnownSurface(target.surfaceType) : undefined,
            displayName: target.displayName ?? undefined,
          })
        : api.containers.channels.addUser(containerId, {
            platform,
            providerUserId: target.id,
            blocked,
            displayName: target.displayName ?? undefined,
          }),
    onSuccess: (_data, target) => {
      void queryClient.invalidateQueries({ queryKey: ["channels", containerId] });
      void queryClient.invalidateQueries({ queryKey: ["channel-directory", containerId] });
      setAddedKeys((prev) => new Set(prev).add(target.id));
      setManualId("");
      setManualName("");
    },
    onSettled: () => setPendingKey(null),
  });

  const rows =
    (kind === "channel"
      ? dir.data?.channels.map((row) => ({
          id: row.channelOrSpaceId,
          displayName: row.displayName,
          surfaceType: row.surfaceType,
          tracked: row.tracked,
        }))
      : dir.data?.users.map((row) => ({
          id: row.providerUserId,
          displayName: row.displayName,
          surfaceType: undefined,
          tracked: row.tracked,
        }))) ?? [];

  const manualHint = MANUAL_ID_HINT[platform]?.[kind === "channel" ? "channel" : "user"];

  // Raw provider IDs are noise for an admin — surface one only when it's
  // needed to tell two same-named people apart. (No-name rows already show
  // the ID as their title.)
  const duplicateNames = new Map<string, number>();
  if (kind === "user") {
    for (const row of rows) {
      const name = row.displayName?.toLowerCase();
      if (name) duplicateNames.set(name, (duplicateNames.get(name) ?? 0) + 1);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setAddedKeys(new Set());
          setNameFieldShown(false);
        }
        setOpen(next);
      }}
    >
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{kind === "channel" ? "Add channels" : "Add a person"}</DialogTitle>
          <DialogDescription>
            {kind === "channel"
              ? "Pick where Boo should listen before anyone writes there."
              : "Decide how Boo treats someone before their first message."}
          </DialogDescription>
        </DialogHeader>

        {platforms.length > 1 && (
          <div className="flex gap-1">
            {platforms.map((p) => (
              <Button
                key={p.platform}
                variant={platform === p.platform ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setPlatform(p.platform)}
              >
                {PLATFORM_LABEL[p.platform]}
              </Button>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
            <span className="text-sm">
              {kind === "channel" ? "Start it as" : "Should Boo answer them?"}
            </span>
            {kind === "channel" ? (
              <ModePicker value={mode} onChange={setMode} />
            ) : (
              <div className="flex gap-1">
                <Button
                  variant={blocked ? "ghost" : "secondary"}
                  size="sm"
                  onClick={() => setBlocked(false)}
                >
                  Gets answers
                </Button>
                <Button
                  variant={blocked ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setBlocked(true)}
                >
                  Blocked
                </Button>
              </div>
            )}
          </div>
          {kind === "user" && (
            <p className="text-xs text-muted-foreground">
              {blockNewUsers
                ? "New people wait for your approval right now — adding someone as “Gets answers” lets Boo reply to them straight away."
                : "Boo answers anyone right now — add someone as Blocked to make an exception."}
            </p>
          )}
          {kind === "channel" && mode === "never" && (
            <p className="text-xs text-muted-foreground">
              If someone @-mentions Boo there, it sends its not-available reply.
            </p>
          )}
          {kind === "user" && blocked && (
            <p className="text-xs text-muted-foreground">
              If they write to Boo, it sends its not-available reply.
            </p>
          )}
        </div>

        {usePicker ? (
          <>
            <Input
              placeholder={kind === "channel" ? "Search channels…" : "Search people…"}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {/* Fixed height on purpose: filtering must not resize the dialog
                (it re-centers on every height change), and loading/error render
                inside the box so the search input above never unmounts. */}
            <div
              className={`h-80 overflow-y-auto rounded-md border border-border divide-y divide-border transition-opacity ${
                dir.isPlaceholderData ? "opacity-60" : ""
              }`}
            >
              {dir.isLoading ? (
                <LoadingIndicator message="Loading directory…" />
              ) : dir.isError ? (
                <InlineError error={dir.error} />
              ) : rows.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">
                  {progressMessage ?? "No matches."}
                </p>
              ) : (
                rows.map((row) => (
                  <div key={row.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm">{row.displayName ?? row.id}</span>
                      {(() => {
                        const sub =
                          row.surfaceType !== undefined
                            ? (ROW_SURFACE_LABEL[row.surfaceType] ?? row.surfaceType)
                            : row.displayName &&
                                (duplicateNames.get(row.displayName.toLowerCase()) ?? 0) > 1
                              ? row.id
                              : null;
                        return sub ? (
                          <span className="truncate text-xs text-muted-foreground">{sub}</span>
                        ) : null;
                      })()}
                    </div>
                    {addedKeys.has(row.id) ? (
                      <span className="shrink-0 text-xs text-muted-foreground">Added ✓</span>
                    ) : row.tracked ? (
                      <span className="shrink-0 text-xs text-muted-foreground">Already listed</span>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        disabled={add.isPending}
                        onClick={() => {
                          setPendingKey(row.id);
                          add.mutate(row);
                        }}
                      >
                        {add.isPending && pendingKey === row.id ? "Adding…" : "Add"}
                      </Button>
                    )}
                  </div>
                ))
              )}
              {progressMessage && rows.length > 0 && (
                <p className="p-3 text-xs text-muted-foreground">{progressMessage}</p>
              )}
            </div>
            {(dir.data?.warnings ?? []).map((warning) => (
              <p key={warning} className="text-xs text-amber-600 dark:text-amber-500">
                {warning}
              </p>
            ))}
          </>
        ) : null}
        <div className="flex flex-col gap-2">
          {usePicker && (
            <div className="flex items-center gap-2 pt-1">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">or add by ID</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}
          <Input
            placeholder={kind === "channel" ? "Channel or space ID" : "User ID"}
            value={manualId}
            onChange={(e) => setManualId(e.target.value)}
          />
          {showNameInput && (
            <Input
              placeholder="Name (optional, shown in the list)"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
            />
          )}
          {/* Always-rendered fixed line so lookup results don't push the
              button around while typing. */}
          {kind === "channel" && (
            <p
              className={`min-h-4 text-xs ${
                metaNotFound && !meta.isFetching
                  ? "text-amber-600 dark:text-amber-500"
                  : "text-muted-foreground"
              }`}
            >
              {!lookupCurrent
                ? " "
                : meta.isFetching
                  ? "Checking the ID…"
                  : metaRow
                    ? `Found: ${metaRow.displayName ?? lookupId}${
                        metaRow.surfaceType
                          ? ` · ${ROW_SURFACE_LABEL[metaRow.surfaceType] ?? metaRow.surfaceType}`
                          : ""
                      }`
                    : metaNotFound
                      ? `No match for this ID on ${PLATFORM_LABEL[platform]} — double-check it before adding.`
                      : " "}
            </p>
          )}
          {manualHint && <p className="text-xs text-muted-foreground">{manualHint}</p>}
          <Button
            className="self-end"
            size="sm"
            disabled={add.isPending || manualId.trim().length === 0}
            onClick={() =>
              add.mutate({
                id: manualId.trim(),
                // The live lookup's name/surface ride along when it matched;
                // a hand-typed name wins, but only while its field is visible.
                displayName:
                  (showNameInput ? manualName.trim() : "") || metaRow?.displayName || null,
                surfaceType: metaRow?.surfaceType ?? undefined,
              })
            }
          >
            {add.isPending ? "Adding…" : "Add"}
          </Button>
        </div>
        {add.isError && <InlineError error={add.error} />}
      </DialogContent>
    </Dialog>
  );
}
